import React, { useEffect, useRef, useState } from 'react';
import { ArrowRight, TrendingUp } from 'lucide-react';
import { caseStudies } from '../../data/mock';

const CaseStudies = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [activeStudy, setActiveStudy] = useState(0);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const study = caseStudies[activeStudy];

  return (
    <section
      ref={sectionRef}
      className="relative py-24 md:py-32 bg-[#0A0A0A]"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* Section Header */}
        <div
          className={`text-center mb-16 transition-all duration-1000 ease-out ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <p className="text-[#D4AF37] text-xs tracking-[0.4em] mb-4">SUCCESS STORIES</p>
          <h2 className="font-playfair text-3xl md:text-4xl text-white">
            Proven <span className="italic text-[#D4AF37]">Results</span>
          </h2>
        </div>

        {/* Case Study Tabs */}
        <div
          className={`flex justify-center gap-4 mb-12 transition-all duration-1000 ease-out ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
          style={{ transitionDelay: '200ms' }}
        >
          {caseStudies.map((cs, index) => (
            <button
              key={cs.id}
              onClick={() => setActiveStudy(index)}
              className={`px-6 py-3 text-sm tracking-[0.2em] border transition-all duration-500 ${
                activeStudy === index
                  ? 'border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/10'
                  : 'border-white/10 text-[#A1A1AA] hover:border-[#D4AF37]/50 hover:text-white'
              }`}
            >
              {cs.brand}
            </button>
          ))}
        </div>

        {/* Case Study Content */}
        <div
          className={`grid lg:grid-cols-2 gap-12 transition-all duration-1000 ease-out ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
          style={{ transitionDelay: '400ms' }}
        >
          {/* Left Column - Story */}
          <div className="bg-[#121212] border border-[#D4AF37]/10 p-8 md:p-12">
            <div className="flex items-center gap-3 mb-6">
              <TrendingUp className="text-[#D4AF37]" size={20} />
              <span className="text-[#D4AF37] text-xs tracking-[0.2em]">{study.brand} CAMPAIGN</span>
            </div>

            <h3 className="font-playfair text-2xl md:text-3xl text-white mb-4">
              {study.campaign}
            </h3>

            <p className="text-[#A1A1AA] leading-relaxed mb-8">
              {study.description}
            </p>

            {/* Before / After */}
            <div className="grid grid-cols-2 gap-6 pt-8 border-t border-white/10">
              <div>
                <p className="text-[#A1A1AA] text-xs tracking-[0.2em] mb-3">BEFORE</p>
                <p className="text-white text-lg">{study.before.followers} followers</p>
                <p className="text-[#A1A1AA]">{study.before.engagement} engagement</p>
              </div>
              <div>
                <p className="text-[#D4AF37] text-xs tracking-[0.2em] mb-3">AFTER</p>
                <p className="text-white text-lg">{study.after.followers} followers</p>
                <p className="text-[#D4AF37]">{study.after.engagement} engagement</p>
              </div>
            </div>
          </div>

          {/* Right Column - Results */}
          <div className="flex flex-col justify-center">
            <p className="text-[#A1A1AA] text-xs tracking-[0.2em] mb-8">KEY RESULTS</p>
            
            <div className="space-y-6">
              {study.results.map((result, index) => (
                <div
                  key={result.metric}
                  className="group flex items-center justify-between p-6 bg-[#121212] border border-[#D4AF37]/10 hover:border-[#D4AF37]/40 transition-all duration-500"
                >
                  <div className="flex items-center gap-4">
                    <ArrowRight className="text-[#D4AF37] opacity-0 group-hover:opacity-100 transition-opacity duration-300" size={16} />
                    <span className="text-white">{result.metric}</span>
                  </div>
                  <span className="text-[#D4AF37] text-2xl font-light">{result.value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CaseStudies;
